import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import HomeHeader from './HomeHeader';
import Footer from './Footer';
import { Context } from 'Context/Context';
import { contactUsApi } from 'Constants';
import { toast } from 'react-toastify';
import { Mail, MapPin } from 'lucide-react';

// Shared field styling (matches InputElement.jsx - brass focus ring on white)
const fieldClass = 'w-full rounded-md border border-gray-200 bg-white py-2.5 px-3 text-navy text-sm font-plexsans placeholder:text-[rgba(20,33,61,0.35)] focus:outline-none focus:border-tan focus:ring-1 focus:ring-tan transition-colors';
const labelStyle = { display: 'block', font: "600 12.5px/1 'IBM Plex Sans',sans-serif", color: '#14213d', marginBottom: '7px' };

function ContactUs() {
    const {makeApiCallFunc} = useContext(Context);
    const [contactForm, setContactForm] = useState({
        name: '',
        email: '',
        phone: '',
        message: ''
    })
    const submitForm = (e) => {
        e.preventDefault();
        if(contactForm.name && contactForm.email && contactForm.phone && contactForm.message) {
        makeApiCallFunc(contactUsApi,
            {
                "name" : contactForm?.name
                , "email" : contactForm?.email
                , "phone" : contactForm?.phone
                , "message" : contactForm?.message
            },

            (response) => {
                if(response?.status === 200) {
                    toast.success('Your message has been sent successfully! We will get back to you soon.');
                    setContactForm({
                        name: '',
                        email: '',
                        phone: '',
                        message: ''
                    })
                }
                else {
                    toast.error('There was an error sending your message. Please try again later.');
                }
            },
            (error) => {
                console.log(error)
            },
            null,
            false,
            true
        )
    }
    else {
        toast.error('Please fill all the fields')
    }
    }

    const infoRow = (Icon, children) => (
        <div className='flex items-start' style={{ gap: '14px' }}>
            <div className='flex-none flex items-center justify-center' style={{ width: '36px', height: '36px', borderRadius: '6px', background: 'rgba(138,98,49,.08)', border: '1px solid rgba(138,98,49,.2)' }}>
                <Icon size={16} strokeWidth={1.8} color="#8a6231" />
            </div>
            <div style={{ paddingTop: '2px' }}>{children}</div>
        </div>
    );

  return (
    <div className='w-full'>
        <HomeHeader/>

      <div className='w-full font-plexsans' style={{ background: '#f7f4ed', borderBottom: '1px solid rgba(20,33,61,.08)' }}>
        <div className='mx-auto grid grid-cols-1 lg:grid-cols-[1fr_460px] items-start px-5 py-12 lg:px-10 lg:py-20 gap-10 lg:gap-16' style={{ maxWidth: '1120px' }}>

            {/* Left: intro + direct contact details */}
            <div className='lg:pt-6'>
                <div style={{ font: "600 11px/1 'IBM Plex Mono',monospace", letterSpacing: '.12em', textTransform: 'uppercase', color: '#8a6231', marginBottom: '14px' }}>Contact</div>
                <h1 className='text-[32px] lg:text-[42px]' style={{ margin: '0 0 18px', fontFamily: "'Spectral',serif", fontWeight: 400, lineHeight: 1.15, color: '#14213d' }}>
                    Contact Us
                </h1>
                <p style={{ margin: '0 0 36px', font: "400 15px/1.65 'IBM Plex Sans',sans-serif", color: 'rgba(20,33,61,.6)', maxWidth: '460px' }}>
                    Email, call or complete the form below to get in touch with us. We will respond to you as soon as possible.
                </p>

                <div className='flex flex-col' style={{ gap: '22px' }}>
                    {infoRow(Mail,
                        <>
                            <div style={{ font: "500 11.5px/1 'IBM Plex Sans',sans-serif", color: 'rgba(20,33,61,.5)', marginBottom: '6px' }}>Email</div>
                            <a href='mailto:info@quickcasereview.com' className='hover:text-tanDark transition-colors' style={{ font: "500 14.5px/1.4 'IBM Plex Sans',sans-serif", color: '#8a6231', textDecoration: 'underline', textUnderlineOffset: '3px' }}>
                                info@quickcasereview.com
                            </a>
                        </>
                    )}
                    {infoRow(MapPin,
                        <>
                            <div style={{ font: "500 11.5px/1 'IBM Plex Sans',sans-serif", color: 'rgba(20,33,61,.5)', marginBottom: '6px' }}>Mailing address</div>
                            <div style={{ font: "400 14.5px/1.55 'IBM Plex Sans',sans-serif", color: '#14213d' }}>
                                5473 Blair RD STE 100 # 619812<br/>Dallas, TX 75231
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Right: form card */}
            <div className='bg-white' style={{ border: '1px solid rgba(20,33,61,.12)', borderRadius: '8px', padding: '32px 30px', boxShadow: '0 6px 22px rgba(20,33,61,.08)' }}>
                <h2 style={{ margin: '0 0 4px', font: "400 23px/1.25 'Spectral',serif", color: '#14213d' }}>Get in Touch</h2>
                <p style={{ margin: '0 0 24px', font: "400 13.5px/1.5 'IBM Plex Sans',sans-serif", color: 'rgba(20,33,61,.55)' }}>You can reach us anytime</p>

                <form className='flex flex-col' style={{ gap: '16px' }} onSubmit={(e) => submitForm(e)}>
                    <div>
                        <label htmlFor='contact-name' style={labelStyle}>Name</label>
                        <input id='contact-name' type='text' placeholder='Your full name'
                        value = {contactForm?.name}
                        onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                        className={fieldClass} />
                    </div>
                    <div className='grid grid-cols-1 sm:grid-cols-2' style={{ gap: '16px' }}>
                        <div>
                            <label htmlFor='contact-email' style={labelStyle}>Email</label>
                            <input id='contact-email' type='email' placeholder='you@example.com'
                            value={contactForm?.email}
                            onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                            className={fieldClass} />
                        </div>
                        <div>
                            <label htmlFor='contact-phone' style={labelStyle}>Phone Number</label>
                            <input id='contact-phone' type='text' placeholder='(555) 000-0000'
                            value={contactForm?.phone}
                            onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                            className={fieldClass} />
                        </div>
                    </div>
                    <div>
                        <label htmlFor='contact-message' style={labelStyle}>How can we help?</label>
                        <textarea id='contact-message' rows={5} placeholder='Tell us a little about your question'
                        value={contactForm?.message}
                        onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                        className={fieldClass + ' resize-y'}></textarea>
                    </div>
                    <button type='submit' className='w-full bg-tan hover:bg-tanDark text-ivory transition-colors'
                    style={{ font: "500 13.5px/1 'IBM Plex Sans',sans-serif", padding: '14px', borderRadius: '5px', marginTop: '6px' }}
                    >Submit</button>
                    <div className='text-center' style={{ font: "400 12px/1.6 'IBM Plex Sans',sans-serif", color: 'rgba(20,33,61,.5)' }}>
                        By clicking submit, you agree to our <Link to="/user-agreement" className='text-tan underline underline-offset-2 hover:text-tanDark'>Terms of Service</Link> and <Link to="/privacy-policy" className='text-tan underline underline-offset-2 hover:text-tanDark'>Privacy Policy</Link>
                    </div>
                </form>
            </div>
        </div>
      </div>
      <Footer/>
    </div>
  )
}

export default ContactUs
